import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/SupabaseAuthContext';

export interface TourStep {
  id: string;
  route: string;
  selector: string;
  title: string;
  body: string;
}

const TOUR_VERSION = 'v1';

const OWNER_STEPS: TourStep[] = [
  {
    id: 'dashboard',
    route: '/',
    selector: '[data-tour="dashboard-stats"]',
    title: 'Live operations at a glance',
    body: 'Active jobs, assets, PM tasks, and revenue — all from your live database.',
  },
  {
    id: 'portal-banner',
    route: '/',
    selector: '[data-tour="portal-requests"]',
    title: 'Customer portal requests',
    body: 'When customers submit jobs online, they appear here for fast assignment.',
  },
  {
    id: 'work-orders',
    route: '/work-orders',
    selector: '[data-tour="work-orders-list"]',
    title: 'Work orders hub',
    body: 'Create, filter, and manage every job. Portal submissions show a cyan badge.',
  },
  {
    id: 'sla-queue',
    route: '/work-orders',
    selector: '[data-tour="sla-queue"]',
    title: 'Priority / SLA queue',
    body: 'Urgent jobs with countdown timers. Tap Assign to me to grab a job instantly.',
  },
  {
    id: 'dispatch',
    route: '/gps',
    selector: '[data-tour="dispatch-board"]',
    title: 'Dispatch board',
    body: 'See assignments on the map and dispatch board — updates live without refresh.',
  },
];

const TECH_STEPS: TourStep[] = [
  {
    id: 'my-jobs',
    route: '/work-orders',
    selector: '[data-tour="work-orders-list"]',
    title: 'Your jobs',
    body: 'Open assigned work orders. Use Start on the job or the mobile bar at the bottom.',
  },
  {
    id: 'sla',
    route: '/work-orders',
    selector: '[data-tour="sla-queue"]',
    title: 'Pick up urgent work',
    body: 'Unassigned urgent jobs? Tap Assign to me — no dispatcher needed.',
  },
  {
    id: 'dispatch',
    route: '/gps',
    selector: '[data-tour="dispatch-board"]',
    title: 'Dispatch map',
    body: 'Check where you are relative to today\'s jobs.',
  },
];

function storageKey(role: string) {
  return `tradetitans_tour_${role}_${TOUR_VERSION}`;
}

const OnboardingTour: React.FC = () => {
  const { profile } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);

  const steps =
    profile?.role === 'technician'
      ? TECH_STEPS
      : ['owner', 'supervisor'].includes(profile?.role || '')
        ? OWNER_STEPS
        : [];

  const step = steps[stepIndex];

  useEffect(() => {
    if (!profile?.role || steps.length === 0) return;
    if (localStorage.getItem(storageKey(profile.role))) return;
    const t = setTimeout(() => setOpen(true), 1200);
    return () => clearTimeout(t);
  }, [profile?.role, steps.length]);

  const positionHighlight = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.selector);
    if (el) setRect(el.getBoundingClientRect());
    else setRect(null);
  }, [step]);

  useEffect(() => {
    if (!open || !step) return;
    if (location.pathname !== step.route && !location.pathname.startsWith(step.route + '/')) return;
    const t = setTimeout(positionHighlight, 400);
    window.addEventListener('resize', positionHighlight);
    window.addEventListener('scroll', positionHighlight, true);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', positionHighlight);
      window.removeEventListener('scroll', positionHighlight, true);
    };
  }, [open, step, location.pathname, positionHighlight]);

  const finish = () => {
    if (profile?.role) localStorage.setItem(storageKey(profile.role), 'done');
    setOpen(false);
  };

  const next = () => {
    if (stepIndex >= steps.length - 1) {
      finish();
      return;
    }
    setStepIndex((i) => i + 1);
    setRect(null);
  };

  const prev = () => {
    if (stepIndex > 0) {
      setStepIndex((i) => i - 1);
      setRect(null);
    }
  };

  if (!open || !step || steps.length === 0) return null;

  const onCorrectRoute =
    location.pathname === step.route || location.pathname.startsWith(step.route + '/');

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none">
      <div className="absolute inset-0 bg-black/60 pointer-events-auto" onClick={finish} />
      {rect && onCorrectRoute && (
        <div
          className="absolute rounded-lg ring-2 ring-cyan-400 ring-offset-2 ring-offset-transparent shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] pointer-events-none"
          style={{
            top: rect.top - 4,
            left: rect.left - 4,
            width: rect.width + 8,
            height: rect.height + 8,
          }}
        />
      )}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-[min(420px,calc(100%-2rem))] pointer-events-auto">
        <div className="bg-slate-900 border border-slate-600 rounded-xl p-4 shadow-2xl">
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              Quick tour {stepIndex + 1}/{steps.length}
            </div>
            <button type="button" onClick={finish} className="text-slate-500 hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="text-white font-semibold">{step.title}</h3>
          <p className="text-slate-400 text-sm mt-1 leading-relaxed">{step.body}</p>
          {!onCorrectRoute && (
            <p className="text-amber-400/90 text-xs mt-2">
              Open <strong>{step.route === '/' ? 'Dashboard' : step.route}</strong> to see this highlight.
            </p>
          )}
          <div className="flex justify-between mt-4 gap-2">
            <Button variant="outline" size="sm" className="border-slate-600" onClick={prev} disabled={stepIndex === 0}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="text-slate-400" onClick={finish}>
              Skip tour
            </Button>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={next}>
              {stepIndex >= steps.length - 1 ? 'Done' : 'Next'}
              {stepIndex < steps.length - 1 && <ChevronRight className="w-4 h-4 ml-1" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;